import React, { useEffect, useRef } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import PauseRoundedIcon from '@mui/icons-material/PauseRounded';
import UnarchiveOutlinedIcon from '@mui/icons-material/UnarchiveOutlined';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import RemoveCircleOutlineRoundedIcon from '@mui/icons-material/RemoveCircleOutlineRounded';
import { BulkAction, BulkActionLogEntry, BulkActionStatus } from './hooks/types';
import {
  closeHeaderButtonSx,
  closeHeaderIconSx,
  dialogBackdropSx,
  dialogContentSx,
  dialogPaperSx,
  doneButtonSx,
  emptyLogSx,
  failedRailSx,
  footerSx,
  headerSx,
  headerTextSx,
  iconTileBaseSx,
  logContainerSx,
  logNameSx,
  logReasonSx,
  logRowSx,
  progressRailSx,
  progressWrapSx,
  resultIconSx,
  skippedIconSx,
  skippedRailSx,
  spinnerSx,
  stopButtonSx,
  subtitleSx,
  successRailSx,
  titleSx,
  Tone,
  toneStyles,
} from './ModalBulkActionProgress.styles';

const ACTION_META: Record<BulkAction, { verb: string; icon: React.ReactNode; tone: Tone }> = {
  activate: { verb: 'Activate', icon: <PlayArrowRoundedIcon />, tone: 'success' },
  pause: { verb: 'Pause', icon: <PauseRoundedIcon />, tone: 'warning' },
  unarchive: { verb: 'Unarchive', icon: <UnarchiveOutlinedIcon />, tone: 'neutral' },
};

interface ModalBulkActionProgressProps {
  open: boolean;
  action: BulkAction | null;
  total: number;
  applicable: number;
  completed: number;
  succeeded: number;
  failed: number;
  skipped: number;
  status: BulkActionStatus;
  log: BulkActionLogEntry[];
  currentName?: string;
  onCancel: () => void;
  onClose: () => void;
}

const getSubtitle = ({
  isFinished,
  isCancelling,
  succeeded,
  failed,
  skipped,
  status,
  currentName,
  applicable,
}: {
  isFinished: boolean;
  isCancelling: boolean;
  succeeded: number;
  failed: number;
  skipped: number;
  status: BulkActionStatus;
  currentName?: string;
  applicable: number;
}) => {
  if (isFinished) {
    return (
      `${succeeded} succeeded` +
      `${failed ? `, ${failed} failed` : ''}` +
      `${skipped ? `, ${skipped} skipped` : ''}` +
      `${status === 'cancelled' ? ' · stopped' : ''}`
    );
  }

  if (isCancelling) return 'Stopping… finishing the current flow';
  if (currentName) return `Now processing “${currentName}”`;
  return `${succeeded + failed} of ${applicable} processed`;
};

const getLogReason = (entry: BulkActionLogEntry) => {
  if (entry.outcome === 'failed') return entry.reason;
  if (entry.outcome === 'skipped') return entry.reason || 'skipped';
  return undefined;
};

const ResultIcon: React.FC<{ outcome: BulkActionLogEntry['outcome'] }> = ({ outcome }) => {
  if (outcome === 'ok') {
    return (
      <Box sx={resultIconSx('ok')}>
        <CheckRoundedIcon sx={{ fontSize: 12 }} />
      </Box>
    );
  }
  if (outcome === 'failed') {
    return (
      <Box sx={resultIconSx('failed')}>
        <CloseRoundedIcon sx={{ fontSize: 12 }} />
      </Box>
    );
  }
  return <RemoveCircleOutlineRoundedIcon sx={skippedIconSx} />;
};

const ModalBulkActionProgress: React.FC<ModalBulkActionProgressProps> = ({
  open,
  action,
  applicable,
  succeeded,
  failed,
  skipped,
  status,
  log,
  currentName,
  onCancel,
  onClose,
}) => {
  const isFinished = status === 'done' || status === 'cancelled';
  const isCancelling = status === 'cancelling';
  const meta = action ? ACTION_META[action] : ACTION_META.activate;
  const tone = toneStyles(meta.tone);

  const verbing = meta.verb.replace(/e?$/, 'ing');

  const title = isFinished
    ? `${meta.verb} complete`
    : `${verbing} ${applicable} flow${applicable === 1 ? '' : 's'}…`;

  const subtitle = getSubtitle({
    isFinished,
    isCancelling,
    succeeded,
    failed,
    skipped,
    status,
    currentName,
    applicable,
  });

  const barDenom = applicable > 0 ? applicable : 1;
  const pct = (n: number) => (n / barDenom) * 100;
  const allSkipped = isFinished && applicable === 0;

  const logRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = logRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [log.length]);

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      onClose={isFinished ? onClose : undefined}
      disableEscapeKeyDown={!isFinished}
      slotProps={{
        backdrop: {
          sx: dialogBackdropSx,
        },
      }}
      PaperProps={{ sx: dialogPaperSx }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center" sx={headerSx}>
        <Box
          sx={{
            ...iconTileBaseSx,
            bgcolor: tone.bg,
            color: tone.color,
          }}
        >
          {isCancelling || (!isFinished && !currentName) ? (
            <CircularProgress size={16} thickness={5} sx={spinnerSx} />
          ) : (
            meta.icon
          )}
        </Box>
        <Box sx={headerTextSx}>
          <Typography sx={titleSx} noWrap>
            {title}
          </Typography>
          <Typography sx={subtitleSx} noWrap title={subtitle}>
            {subtitle}
          </Typography>
        </Box>
        {isFinished && (
          <IconButton onClick={onClose} size="small" aria-label="Close" sx={closeHeaderButtonSx}>
            <CloseRoundedIcon sx={closeHeaderIconSx} />
          </IconButton>
        )}
      </Stack>

      <DialogContent sx={dialogContentSx}>
        <Box sx={progressWrapSx}>
          <Box sx={progressRailSx}>
            {allSkipped ? (
              <Box sx={skippedRailSx} />
            ) : (
              <>
                <Box sx={{ ...successRailSx, width: `${pct(succeeded)}%` }} />
                <Box sx={{ ...failedRailSx, width: `${pct(failed)}%` }} />
              </>
            )}
          </Box>
        </Box>

        <Box ref={logRef} sx={logContainerSx}>
          <Stack spacing={0.25}>
            {log.map((entry, index) => {
              const reason = getLogReason(entry);
              return (
                <Stack
                  key={`${entry.name ?? 'flow'}-${index}`}
                  direction="row"
                  spacing={1.25}
                  alignItems="center"
                  sx={logRowSx}
                >
                  <ResultIcon outcome={entry.outcome} />
                  <Typography
                    variant="body2"
                    noWrap
                    sx={{
                      ...logNameSx,
                      color: entry.outcome === 'skipped' ? 'text.secondary' : 'text.primary',
                    }}
                    title={entry.name}
                  >
                    {entry.name || 'Flow'}
                  </Typography>
                  {reason && (
                    <Typography
                      variant="caption"
                      noWrap
                      sx={{
                        ...logReasonSx,
                        color: entry.outcome === 'failed' ? 'error.main' : 'text.disabled',
                      }}
                    >
                      {reason}
                    </Typography>
                  )}
                </Stack>
              );
            })}
            {log.length === 0 && (
              <Typography variant="caption" sx={emptyLogSx}>
                Starting…
              </Typography>
            )}
          </Stack>
        </Box>
      </DialogContent>

      <Stack
        direction="row"
        justifyContent="flex-end"
        alignItems="center"
        spacing={1}
        sx={footerSx}
      >
        {isFinished ? (
          <Button onClick={onClose} variant="contained" disableElevation sx={doneButtonSx}>
            Done
          </Button>
        ) : (
          <Button
            onClick={onCancel}
            variant="contained"
            color="error"
            disableElevation
            disabled={isCancelling}
            startIcon={<CloseRoundedIcon />}
            sx={stopButtonSx}
          >
            {isCancelling ? 'Stopping…' : 'Stop'}
          </Button>
        )}
      </Stack>
    </Dialog>
  );
};

export default ModalBulkActionProgress;
